import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { CatalogNearMe } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { defaultHitsPerPage } from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { GeoArea } from '@/types/GeoArea';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { RelatedGeoAreasNew } from '@/components/RelatedGeoAreas/RelatedGeoAreasNew';
import { useAppDispatch } from '@/redux/hooks';
import { useEffect, useMemo, useState } from 'react';
import AuctionsNearMeCatalogGrid from './AuctionsNearMeCatalogGrid';
import AuctionsNearMeFilterBar from './components/FilterBar/AuctionsNearMeFilterBar';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';

export type Props = {
    catalogsNearMe: CatalogNearMe[];
    geoArea?: GeoArea;
};

export const AuctionsNearMeCardsViewNew = ({ catalogsNearMe, geoArea }: Props) => {
    const dispatch = useAppDispatch();
    // Initialize loading based on whether we have SSR data - allows content to render server-side
    const [isLoading, setIsLoading] = useState(catalogsNearMe.length === 0);
    const [pageNumber, setPageNumber] = useState(1);
    const [perPage, setPerPage] = useState(defaultHitsPerPage);
    const [keywords, setKeywords] = useState<string[]>([]);
    const [localPickUpFilter, setLocalPickUpFilter] = useState(false);
    const [topRatedFilter, setTopRatedFilter] = useState(false);

    // Apply filters to catalogs
    const filteredCatalogs = useMemo(() => {
        let filtered = catalogsNearMe;

        // Keyword filter
        if (keywords.length > 0) {
            filtered = filtered.filter((catalog) => {
                const titleLower = catalog.title.toLowerCase();
                const descLower = catalog.description.toLowerCase();
                return keywords.some((keyword) => {
                    const keywordLower = keyword.toLowerCase();
                    return titleLower.includes(keywordLower) || descLower.includes(keywordLower);
                });
            });
        }

        // Local pickup filter
        if (localPickUpFilter) {
            filtered = filtered.filter((catalog) => catalog.hasFreeLocalPickup);
        }

        // Top rated filter
        if (topRatedFilter) {
            filtered = filtered.filter((catalog) => catalog.houseDetails.isTopRated);
        }

        return filtered;
    }, [catalogsNearMe, keywords, localPickUpFilter, topRatedFilter]);

    // Extract catalog IDs from filtered results
    const filteredCatalogIds = useMemo(() => {
        return filteredCatalogs.map((catalog) => catalog.id);
    }, [filteredCatalogs]);

    // Slice catalog IDs for current page
    const currentPageCatalogIds = useMemo(() => {
        return filteredCatalogIds.slice((pageNumber - 1) * perPage, pageNumber * perPage);
    }, [filteredCatalogIds, pageNumber, perPage]);

    // Fetch full catalog details only for the current page slice
    useEffect(() => {
        if (currentPageCatalogIds.length > 0) {
            dispatch(fetchCatalogsIfNeeded({ catalogIds: currentPageCatalogIds }));
        }
    }, [currentPageCatalogIds, dispatch]);

    useEffect(() => {
        setIsLoading(false);
    }, [catalogsNearMe]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pageNumber]);

    const toggleLocalPickUpFilter = () => {
        setLocalPickUpFilter(!localPickUpFilter);
    };

    const toggleTopRatedFilter = () => {
        setTopRatedFilter(!topRatedFilter);
    };

    const handlePaginationChange = ({ page, pageSize: newPageSize }: { page: number; pageSize?: number }) => {
        if (page !== pageNumber) {
            setPageNumber(page);
        }
        if (newPageSize && newPageSize !== perPage) {
            setPerPage(newPageSize);
        }
    };

    const catalogsView = (
        <section>
            <Pagination
                onChange={handlePaginationChange}
                pageSizeOptions={[24, 48, 72, 96, 120]}
                paginationFilter={{
                    page: pageNumber,
                    pageSize: perPage,
                    totalRecords: filteredCatalogIds.length,
                }}
            >
                <AuctionsNearMeCatalogGrid catalogIds={currentPageCatalogIds} />
            </Pagination>
        </section>
    );

    const noCatalogsView = (
        <section>
            <BodyPrimary>
                <FormattedMessage id="no_auctions_available_in_area" />
            </BodyPrimary>
        </section>
    );

    const results = filteredCatalogIds.length > 0 ? catalogsView : noCatalogsView;

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : (
                <div>
                    {/* Sticky Header */}
                    <div className="smMin:sticky smMin:top-0 smMin:z-[11] smMin:bg-background-primary smMin:pb-sm smMin:pt-lg">
                        <AuctionsNearMeFilterBar
                            numberOfAuctions={filteredCatalogIds.length}
                            onKeywordsChange={setKeywords}
                            toggleLocalPickUpFilter={toggleLocalPickUpFilter}
                            toggleTopRatedFilter={toggleTopRatedFilter}
                        />
                    </div>
                    {/* Cards */}
                    {results}
                    {/* "More Locations" */}
                    <div className="mt-2xl">
                        <RelatedGeoAreasNew geoArea={geoArea} />
                    </div>
                </div>
            )}
        </>
    );
};
